import { Eye, EyeOff, Home, Users } from 'lucide-react';

export const DATE_VISIBILITY_VALUES = [
  'PUBLIC',
  'FAMILY_ONLY',
  'FRIENDS_AND_FAMILY',
  'NONE',
] as const;

export type DateVisibility = (typeof DATE_VISIBILITY_VALUES)[number];

export const DATE_VISIBILITY_OPTIONS: {
  value: DateVisibility;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { value: 'PUBLIC', label: 'Public', icon: Eye },
  { value: 'FAMILY_ONLY', label: 'Family only', icon: Home },
  { value: 'FRIENDS_AND_FAMILY', label: 'Friends and family', icon: Users },
  { value: 'NONE', label: 'None', icon: EyeOff },
];

export function dateVisibilityLabel(value?: string | null): string {
  return DATE_VISIBILITY_OPTIONS.find((o) => o.value === value)?.label || 'Public';
}

export type ProfileVisibilityField =
  | 'email'
  | 'phone'
  | 'gender'
  | 'bloodGroup'
  | 'address'
  | 'city'
  | 'pincode'
  | 'dob'
  | 'anniversary';

export function coerceDateVisibility(value?: string | null): DateVisibility {
  if (value && DATE_VISIBILITY_VALUES.includes(value as DateVisibility)) {
    return value as DateVisibility;
  }
  return 'PUBLIC';
}

/** Owner always sees; strangers only see PUBLIC (family/friends gates need relation context). */
export function canViewerSeeField(
  visibility: string | null | undefined,
  opts: { isOwner: boolean }
): boolean {
  if (opts.isOwner) return true;
  return coerceDateVisibility(visibility) === 'PUBLIC';
}

/** Resolve visibility for a profile field from fieldVisibility JSON + legacy columns. */
export function getFieldVisibility(
  user: {
    fieldVisibility?: Partial<Record<ProfileVisibilityField, string>> | null;
    dobVisibility?: string | null;
    anniversaryVisibility?: string | null;
    emailVisibility?: string | null;
    phoneVisibility?: string | null;
    genderVisibility?: string | null;
    bloodGroupVisibility?: string | null;
    addressVisibility?: string | null;
  },
  field: ProfileVisibilityField
): DateVisibility {
  const fromJson = user.fieldVisibility?.[field];
  if (fromJson && DATE_VISIBILITY_VALUES.includes(fromJson as DateVisibility)) {
    return fromJson as DateVisibility;
  }
  const legacy: Partial<Record<ProfileVisibilityField, string | null | undefined>> = {
    email: user.emailVisibility,
    phone: user.phoneVisibility,
    gender: user.genderVisibility,
    bloodGroup: user.bloodGroupVisibility,
    address: user.addressVisibility,
    dob: user.dobVisibility,
    anniversary: user.anniversaryVisibility,
  };
  const fromLegacy = legacy[field];
  if (fromLegacy && DATE_VISIBILITY_VALUES.includes(fromLegacy as DateVisibility)) {
    return fromLegacy as DateVisibility;
  }
  return 'PUBLIC';
}

type AddressParts = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  googleMapLink?: string | null;
};

/**
 * address = street/area/state/country/maps.
 * city + pincode are independent (can stay PUBLIC while address is NONE).
 */
export function formatVisibleAddress(
  address: AddressParts | null | undefined,
  user: Parameters<typeof getFieldVisibility>[0],
  opts: { isOwner: boolean }
): { text: string; mapLink?: string } | null {
  if (!address) return null;

  const showDetails = canViewerSeeField(getFieldVisibility(user, 'address'), opts);
  const showCity = canViewerSeeField(getFieldVisibility(user, 'city'), opts);
  const showPincode = canViewerSeeField(getFieldVisibility(user, 'pincode'), opts);

  const parts = [
    showDetails ? address.line1 : null,
    showDetails ? address.line2 : null,
    showCity ? address.city : null,
    showDetails ? address.state : null,
    showPincode ? address.pincode : null,
    showDetails ? address.country : null,
  ].filter((p): p is string => Boolean(p && String(p).trim()));

  if (parts.length === 0) return null;

  return {
    text: parts.join(', '),
    mapLink: showDetails && address.googleMapLink ? address.googleMapLink : undefined,
  };
}
